import {
  Input,
  Table,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  InputGroup,
  Text,
  Tbody,
  IconButton,
  useToast,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import { getIn, setIn } from 'formik';
import _ from 'lodash';
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { omit } from 'remeda';

import { utils as commonUtils } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { ReportingGlobalRecurringDateConfigs } from '../../property/globalRecurringDate/ReportingGlobalRecurringDateConfigs';
import { RecurringDateConfigView } from '../../property/RecurringDateConfigView';
import { PostgresQueriesView } from '../covenantPostgresQuery/PostgresQueryView';
import {
  addPortfolioCovenantConfig,
  AddPortfolioCovenantModal,
} from './AddPortfolioCovenantModal';
import { PortfolioCovenantEditCovenantValueContainer } from './PortfolioCovenantEditCovenantValueContainer';
import { AthenaQueriesView } from 'packages/athena/ui/AthenaQueryView';
import { AthenaRecurringQueriesContainer } from 'packages/athena/ui/AthenaRecurringQueryView';
import { FormFieldLabel } from 'packages/common-frontend/components/FormField';
import {
  AddIconButton,
  DeleteIconButton,
  EditIconButton,
  SaveIconButton,
  UploadIconButton,
  ResetIconButton,
  CopyIconButton,
} from 'packages/common-frontend/components/IconButton';
import { formatSelectOptions } from 'packages/common-frontend/components/Select';
import {
  useEvaluatePortfolioCovenantsMutation,
  useEvaluateDevPortfolioCovenantWorkleaseMutation,
} from 'packages/creditAgreements/requests/requests.gql.generated';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteArrayPropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { EditPropertyModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditPropertyModal';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { getErrorMessageFromApolloError } from '~/contexts/ApolloClientProvider';

export const deletePortfolioCovenantConfig = (
  wc: WipConfig,
  fullPropertyKey: string,
): void => {
  const covenant = getIn(wc.formProps.values, fullPropertyKey);
  wc.formProps.setFieldValue(fullPropertyKey, omit(covenant, ['_config_']));
};

type PortfolioCovenantConfigProps = {
  organizationId: string;
  _config_?: CreditAgreement.Config.PortfolioCovenant.Config;
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  headersLength: number;
  triggerDate: Util.FullDateStr;
  covenantKey: string;
};
export const PortfolioCovenantConfig = ({
  organizationId,
  _config_,
  wipConfig,
  fullPropertyKey: portfolioCovenantConfigFullPropertyKey,
  headersLength,
  triggerDate,
  covenantKey,
}: PortfolioCovenantConfigProps) => {
  const toast = useToast();

  const [
    evaluatePortfolioCovenants,
    { loading: isEvaluatePortfolioCovenantsQALoading },
  ] = useEvaluatePortfolioCovenantsMutation({
    onCompleted: () => {
      toast({
        title:
          'Initiated portfolio covenants evaluation based on latest SAVED WIP.  Please check your email in a few minutes.',
        duration: 3000,
        status: 'success',
        position: 'top',
      });
    },
    onError: (error) => {
      toast({
        title: getErrorMessageFromApolloError({
          error,
          defaultMessage:
            'Unable to initiate portfolio covenants evaluation. Please see technical assistance.',
        }),
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    },
  });

  const [
    evaluateDevPortCovWorklease,
    { loading: isEvaluateDevPortCovLoading },
  ] = useEvaluateDevPortfolioCovenantWorkleaseMutation({
    onCompleted: ({ result }) => {
      const { response } = result;
      toast({
        title: '[DEV] Evaluate Port Cov Worklease',
        description: `Evaluated Port Cov with response: ${response}`,
        status: 'success',
        position: 'top',
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: '[DEV] Evaluate Port Cov Worklease',
        description: getErrorMessageFromApolloError({
          error,
          defaultMessage:
            'Incurred error during Port Cov evalution, please check logs!!!',
        }),
        status: 'error',
        position: 'top',
        duration: 8000,
        isClosable: true,
      });
    },
  });

  const handleEvaluatePortfolioCovenantsQA = useCallback(
    async (wc: WipConfig) => {
      const creditAgreementId = getIn(wc.formProps.values, 'id');
      const creditFacilityId = getIn(wc.formProps.values, 'creditFacilityId');

      await evaluatePortfolioCovenants({
        variables: {
          evaluatePortfolioCovenantsRequest: {
            covenantKeys: [covenantKey],
            creditAgreementId,
            creditFacilityId,
            triggerDate,
            mode: 'QA',
          },
        },
      });
    },
    [evaluatePortfolioCovenants, triggerDate, covenantKey],
  );

  const handleEvaluateDevPortCov = (wc: WipConfig): void => {
    const creditAgreementId = getIn(wc.formProps.values, 'id');
    const creditFacilityId = getIn(wc.formProps.values, 'creditFacilityId');

    evaluateDevPortCovWorklease({
      variables: {
        worklease: {
          covenantKeys: [covenantKey],
          creditAgreementId,
          creditFacilityId,
          triggerDate,
          mode: 'QA',
        },
      },
    });
  };

  if (!_config_) {
    return (
      <>
        {wipConfig?.isMutateAllowed(portfolioCovenantConfigFullPropertyKey) ? (
          <Tr>
            <Td colSpan={headersLength}>
              <Flex alignItems="center">
                <Text>Add Configuration</Text>
                <AddIconButton
                  ariaLabel="add config"
                  label="Add Configuration"
                  onClick={() => {
                    addPortfolioCovenantConfig(
                      wipConfig,
                      portfolioCovenantConfigFullPropertyKey,
                    );
                  }}
                />
              </Flex>
            </Td>
          </Tr>
        ) : (
          <Tr>
            <Td>
              <Text>No Configuration Exists</Text>
            </Td>
          </Tr>
        )}
      </>
    );
  }

  const presetVariables = [
    { name: 'triggerDate', value: triggerDate, parseNumber: true },
  ];

  return (
    <Tr>
      <Td colSpan={headersLength}>
        <Flex flexDir="column">
          <Flex alignItems="center" mb="3">
            <Text fontSize="large" fontWeight="bold" mr="2">
              Config
            </Text>
            {wipConfig?.isMutateAllowed(
              portfolioCovenantConfigFullPropertyKey,
            ) && (
              <>
                <DeleteIconButton
                  ariaLabel="delete config"
                  label="Delete Configuration"
                  onClick={() => {
                    deletePortfolioCovenantConfig(
                      wipConfig,
                      portfolioCovenantConfigFullPropertyKey,
                    );
                  }}
                />
                <SaveIconButton
                  ariaLabel="save config"
                  label="Save Configuration"
                  onClick={() => {
                    wipConfig.saveCreditAgreementFullArtifactWip({
                      variables: {
                        creditAgreementFullArtifactWip: {
                          id: wipConfig.formProps.values.id,
                          creditFacilityId:
                            wipConfig.formProps.values.creditFacilityId,
                          creditAgreement: JSON.stringify(
                            wipConfig.formProps.values,
                          ),
                        },
                      },
                    });
                  }}
                  isLoading={
                    wipConfig.isSaveCreditAgreementFullArtifactWipLoading
                  }
                />
                <UploadIconButton
                  ariaLabel="qa portfolio covenant"
                  label="QA Portfolio Covenant"
                  onClick={() => handleEvaluatePortfolioCovenantsQA(wipConfig)}
                  isDisabled={isEvaluatePortfolioCovenantsQALoading}
                />

                {/*
                 * Only exposing this QA tool on Dev since don't want to lock
                 * up FinleyCore when evaluating port covs
                 */}
                {commonUtils.EnvUtils.isDevelopment() &&
                  (isEvaluateDevPortCovLoading ? (
                    <Spinner size="sm" mx="2" p="2" />
                  ) : (
                    <ResetIconButton
                      ariaLabel="qa portcov"
                      label="QA PortCov (Dev)"
                      onClick={() => handleEvaluateDevPortCov(wipConfig)}
                    />
                  ))}
              </>
            )}
          </Flex>
          <RecurringDateConfigView
            recurringDateConfig={_config_.triggerDates}
            wipConfig={wipConfig}
            fullPropertyKey={`${portfolioCovenantConfigFullPropertyKey}._config_.triggerDates`}
            displayName="Trigger Dates"
            enableEdit
          />
          <ReportingGlobalRecurringDateConfigs
            wipConfig={wipConfig}
            globalRecurringDateConfigs={_config_.globalRecurringDateConfigs}
            basePropertyKey={`${portfolioCovenantConfigFullPropertyKey}._config_.globalRecurringDateConfigs`}
            source="portCovs"
          />
          <AthenaQueriesView
            organizationId={organizationId}
            athenaQueries={_config_.athenaQueries}
            wipConfig={wipConfig}
            basePropertyKey={`${portfolioCovenantConfigFullPropertyKey}._config_.athenaQueries`}
            inputDateType="triggerDate"
            presetVariables={presetVariables}
            variableSubTitle={
              '"triggerDate" will be an available variable by default that can be used'
            }
          />
          <AthenaRecurringQueriesContainer
            organizationId={organizationId}
            wipConfig={wipConfig}
            athenaRecurringQueries={_config_.athenaRecurringQueries}
            globalRecurringDateConfigs={_config_.globalRecurringDateConfigs}
            basePropertyKey={`${portfolioCovenantConfigFullPropertyKey}._config_.athenaRecurringQueries`}
            inputDateType="triggerDate"
            presetVariables={presetVariables}
            variableSubTitle={
              '"triggerDate" will be an available variable by default that can be used'
            }
          />
          <PostgresQueriesView
            wipConfig={wipConfig}
            triggerDate={triggerDate}
            postgresQueries={_config_.postgresQueries}
            basePropertyKey={`${portfolioCovenantConfigFullPropertyKey}._config_.postgresQueries`}
            inputDateType="triggerDate"
            presetVariables={presetVariables}
            selectOptions={formatSelectOptions(
              CreditAgreement.Config.PortfolioCovenant.PostgresQuery
                .QueryTypeIterable,
              'title',
            )}
          />

          <PortfolioCovenantEditCovenantValueContainer
            wipConfig={wipConfig}
            fullPropertyKey={portfolioCovenantConfigFullPropertyKey}
            triggerDate={triggerDate}
            intermediateFields={_config_.intermediateFields}
          />
        </Flex>
      </Td>
    </Tr>
  );
};

type PortfolioCovenantRowProps = {
  organizationId: string;
  portfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty;
  wipConfig?: WipConfig;
  index: number;
  basePropertyKey: string;
  fullPropertyKey: string;
  headersLength: number;
  triggerDate: Util.FullDateStr;
};
const PortfolioCovenantRow = ({
  organizationId,
  portfolioCovenant,
  wipConfig,
  index,
  basePropertyKey,
  fullPropertyKey,
  headersLength,
  triggerDate,
}: PortfolioCovenantRowProps) => {
  const navigate = useNavigate();
  const {
    isOpen: isDuplicateModalOpen,
    onOpen: onDuplicateModalOpen,
    onClose: onDuplicateModalClose,
  } = useDisclosure();

  const [isOpen, setIsOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isDuplicateLoading, setIsDuplicateLoading] = useState(false);

  const {
    name,
    covenantKey,
    section,
    description,
    _config_,
  } = portfolioCovenant;

  const isClientWipMode = wipConfig?.operationMode === 'client';

  const duplicatePortfolioCovenant = useCallback(
    async ({
      newName,
      newCovenantKey,
    }: {
      newName: string;
      newCovenantKey: string;
    }): Promise<void> => {
      setIsDuplicateLoading(true);
      if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
        return;
      }

      const existingPortfolioCovenants = getIn(
        wipConfig.formProps.values,
        basePropertyKey,
      );

      const copiedPortfolioCovenant = _.cloneDeep(portfolioCovenant);
      copiedPortfolioCovenant.covenantKey = newCovenantKey;
      copiedPortfolioCovenant.name = newName;

      const newPortfolioCovenants = [
        ...existingPortfolioCovenants,
        copiedPortfolioCovenant,
      ];

      const updatedWipCreditAgreement = setIn(
        wipConfig.formProps.values,
        basePropertyKey,
        newPortfolioCovenants,
      );

      const saveData = await wipConfig.saveCreditAgreementFullArtifactWip({
        variables: {
          creditAgreementFullArtifactWip: {
            id: wipConfig.formProps.values.id,
            creditFacilityId: wipConfig.formProps.values.creditFacilityId,
            creditAgreement: JSON.stringify(updatedWipCreditAgreement),
          },
        },
        notifyOnNetworkStatusChange: true,
      });

      if (saveData.data) {
        setIsDuplicateLoading(false);
        onDuplicateModalClose();

        const creditFacilityId = getIn(
          wipConfig.formProps.values,
          'creditFacilityId',
        );

        if (isClientWipMode) {
          // if successfully able to save new portcov, redirect to edit page for the new portcov
          navigate(
            `/settings/credit-facilities/${creditFacilityId}/portfolio-covenants/edit?covenantKey=${newCovenantKey}`,
          );
        } else {
          wipConfig.formProps.setFieldValue(
            basePropertyKey,
            newPortfolioCovenants,
          );
        }
      } else {
        // if CA could not save, allow user to edit inside the modal and try again
        setIsDuplicateLoading(false);
      }
    },
    [
      basePropertyKey,
      portfolioCovenant,
      wipConfig,
      onDuplicateModalClose,
      navigate,
      isClientWipMode,
    ],
  );

  const onClickEdit = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (isClientWipMode) {
        const creditFacilityId = getIn(
          wipConfig.formProps.values,
          'creditFacilityId',
        );

        navigate(
          `/settings/credit-facilities/${creditFacilityId}/portfolio-covenants/edit?covenantKey=${covenantKey}`,
        );
      } else {
        setIsOpen(true);
        if (event) {
          event.stopPropagation();
        }
      }
    },
    [navigate, isClientWipMode, wipConfig, covenantKey],
  );

  return (
    <>
      <Tr borderTop="1px" borderColor="gray.700" verticalAlign="top">
        <Td>{commonUtils.toSentenceCase(name)}</Td>
        <Td>{covenantKey}</Td>
        <Td>{section}</Td>
        <Td>{description || '-'}</Td>
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <Td>
            <CopyIconButton
              ariaLabel="duplicate portfolio covenant"
              label="Duplicate Portfolio Covenant"
              onClick={() => onDuplicateModalOpen()}
            />
            <AddPortfolioCovenantModal
              isOpen={isDuplicateModalOpen}
              onClose={onDuplicateModalClose}
              onClickAdd={({
                nameInput,
                covenantKeyInput,
              }: {
                nameInput: string;
                covenantKeyInput: string;
              }) =>
                duplicatePortfolioCovenant({
                  newName: nameInput,
                  newCovenantKey: covenantKeyInput,
                })
              }
              initialCovenantKey={`${portfolioCovenant.covenantKey}Copy`}
              initialName={`${portfolioCovenant.name} Copy`}
              isAddLoading={isDuplicateLoading}
              mode="duplicate"
            />
            <EditIconButton
              ariaLabel="edit"
              label="Edit"
              onClick={(event) => onClickEdit(event)}
            />
            <DeleteArrayPropertyButton
              wipConfig={wipConfig}
              basePropertyKey={basePropertyKey}
              idx={index}
              useConfirmationModal
              saveOnDelete
            />

            <EditPropertyModal
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              wipConfig={wipConfig}
              basePropertyKey={fullPropertyKey}
            >
              {(localFormProps) => {
                return (
                  <>
                    <Flex alignItems="flex-end" width="100%">
                      <FormFieldLabel labelWidth="60" label="Covenant Key" />
                      <InputGroup width="100%">
                        <Input
                          value={localFormProps.values.covenantKey}
                          onChange={(e) => {
                            localFormProps.setFieldValue(
                              'covenantKey',
                              e.target.value,
                            );
                          }}
                          width="sm"
                        />
                      </InputGroup>
                    </Flex>
                    <Flex alignItems="flex-end" width="100%">
                      <FormFieldLabel labelWidth="60" label="Value" />
                      <InputGroup width="100%">
                        <Input
                          value={localFormProps.values.value}
                          onChange={(e) => {
                            localFormProps.setFieldValue(
                              'value',
                              e.target.value,
                            );
                          }}
                          width="sm"
                        />
                      </InputGroup>
                    </Flex>
                  </>
                );
              }}
            </EditPropertyModal>
          </Td>
        )}
        {wipConfig?.isMutateAllowed(fullPropertyKey) && !isClientWipMode && (
          <Td>
            {isConfigOpen ? (
              <IconButton
                aria-label="close config"
                icon={<FiChevronUp />}
                onClick={() => setIsConfigOpen(false)}
                variant="ghost"
              />
            ) : (
              <IconButton
                aria-label="display config"
                icon={<FiChevronDown />}
                onClick={() => setIsConfigOpen(true)}
                variant="ghost"
              />
            )}
          </Td>
        )}
      </Tr>
      {isConfigOpen && (
        <PortfolioCovenantConfig
          organizationId={organizationId}
          _config_={_config_}
          wipConfig={wipConfig}
          fullPropertyKey={fullPropertyKey}
          headersLength={headersLength}
          triggerDate={triggerDate}
          covenantKey={covenantKey}
        />
      )}
    </>
  );
};

type PortfolioCovenantTableProps = {
  organizationId: string;
  portfolioCovenants: CreditAgreement.DomainEntity.PortfolioCovenantProperty[];
  wipConfig?: WipConfig;
  basePropertyKey: string;
  triggerDate: Util.FullDateStr;
};
export const PortfolioCovenantTable = ({
  organizationId,
  portfolioCovenants,
  wipConfig,
  basePropertyKey,
  triggerDate,
}: PortfolioCovenantTableProps) => {
  if (!portfolioCovenants.length) {
    return <Text>There are currently no properties in this section.</Text>;
  }

  const _portfolioCovenantTableHeaders = [
    'Name',
    'Covenant Key',
    'Section',
    'Description',
  ];

  const portfolioCovenantTableHeaders = wipConfig
    ? _portfolioCovenantTableHeaders
    : _portfolioCovenantTableHeaders.filter(
        (header) => !['Note', 'Is Written in'].includes(header),
      );

  if (wipConfig) {
    portfolioCovenantTableHeaders.push('Action');
  }

  const isClientWipMode = wipConfig?.operationMode === 'client';

  return (
    <Flex width="100%" overflowY="auto">
      <Table borderWidth="1px">
        <Thead>
          <Tr>
            {portfolioCovenantTableHeaders.map((header) => {
              if (header === 'Name') {
                return (
                  <Th minWidth="20rem" width="20rem" key={header}>
                    {header}
                  </Th>
                );
              }
              if (header === 'Action') {
                return (
                  <Th minWidth="11rem" width="11rem" key={header}>
                    {header}
                  </Th>
                );
              }
              return (
                <Th minWidth="10rem" width="10rem" key={header}>
                  {header}
                </Th>
              );
            })}
            {wipConfig && !isClientWipMode && (
              <Th width="5rem" minWidth="5rem" />
            )}
          </Tr>
        </Thead>
        <Tbody>
          {portfolioCovenants.map((portfolioCovenant, index) => {
            const fullPropertyKey = `${basePropertyKey}[${[index]}]`;
            return (
              <PortfolioCovenantRow
                organizationId={organizationId}
                key={fullPropertyKey}
                portfolioCovenant={portfolioCovenant}
                index={index}
                wipConfig={wipConfig}
                basePropertyKey={basePropertyKey}
                fullPropertyKey={fullPropertyKey}
                // Adding "+1" to factor in header for config toggle
                headersLength={portfolioCovenantTableHeaders.length + 1}
                triggerDate={triggerDate}
              />
            );
          })}
        </Tbody>
      </Table>
    </Flex>
  );
};
